import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "../../config";
import AdminLayout from "./AdminLayout";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import { toast } from "sonner";
import { FileText, Download, CheckCircle, Plus, X } from "lucide-react";

const AdminInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    product_name: "",
    amount: 0,
    amount_eur: 0,
    currency: "EGP"
  });

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/invoices`, { withCredentials: true });
      setInvoices(response.data);
    } catch (error) {
      console.error("Error fetching invoices:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchInvoices(); }, []);

  const downloadInvoice = async (invoiceId) => {
    try {
      const response = await axios.get(`${API}/api/invoices/${invoiceId}/pdf`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${invoiceId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("تم تحميل الفاتورة بنجاح");
    } catch (error) {
      toast.error("فشل في تحميل الفاتورة");
    }
  };

  const handleCreateInvoice = async () => {
    if (!formData.customer_name || !formData.product_name || !formData.amount) {
      toast.error("يرجى ملء جميع الحقول المطلوبة");
      return;
    }
    try {
      await axios.post(`${API}/api/admin/invoices/manual`, formData, { withCredentials: true });
      toast.success("تم إنشاء الفاتورة بنجاح");
      setShowForm(false);
      setFormData({ customer_name: "", customer_email: "", customer_phone: "", product_name: "", amount: 0, amount_eur: 0, currency: "EGP" });
      fetchInvoices();
    } catch (error) {
      toast.error("فشل في إنشاء الفاتورة");
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div data-testid="admin-invoices">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold mb-2">الفواتير</h1>
            <p className="text-muted-foreground">عرض وتحميل وإنشاء الفواتير</p>
          </div>
          <Button onClick={() => setShowForm(!showForm)} className="gap-2">
            {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {showForm ? "إلغاء" : "فاتورة يدوية"}
          </Button>
        </motion.div>

        {showForm && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
            <Card className="mb-6">
              <CardContent className="p-6">
                <h2 className="text-xl font-bold mb-4">إنشاء فاتورة يدوية</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>اسم العميل *</Label>
                    <Input value={formData.customer_name} onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })} placeholder="اسم العميل" />
                  </div>
                  <div className="space-y-2">
                    <Label>البريد الإلكتروني</Label>
                    <Input type="email" value={formData.customer_email} onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })} placeholder="email@example.com" dir="ltr" />
                  </div>
                  <div className="space-y-2">
                    <Label>رقم التليفون</Label>
                    <Input value={formData.customer_phone} onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })} placeholder="+20 1xx xxx xxxx" dir="ltr" />
                  </div>
                  <div className="space-y-2">
                    <Label>المنتج / الخدمة *</Label>
                    <Input value={formData.product_name} onChange={(e) => setFormData({ ...formData, product_name: e.target.value })} placeholder="اسم المنتج أو الخدمة" />
                  </div>
                  <div className="space-y-2">
                    <Label>المبلغ بالجنيه (EGP) *</Label>
                    <Input type="number" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) })} min="0" />
                  </div>
                  <div className="space-y-2">
                    <Label>المبلغ باليورو (EUR)</Label>
                    <Input type="number" value={formData.amount_eur} onChange={(e) => setFormData({ ...formData, amount_eur: parseFloat(e.target.value) })} min="0" />
                  </div>
                </div>
                <Button onClick={handleCreateInvoice} className="mt-4 gap-2">
                  <Plus className="w-4 h-4" />
                  إنشاء الفاتورة
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        )}

        <Card>
          <CardContent className="p-0">
            {invoices.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">لا توجد فواتير حتى الآن</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>رقم الفاتورة</TableHead>
                    <TableHead>العميل</TableHead>
                    <TableHead>المنتج</TableHead>
                    <TableHead>المبلغ</TableHead>
                    <TableHead>الحالة</TableHead>
                    <TableHead>التاريخ</TableHead>
                    <TableHead>الإجراءات</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices?.map((invoice, idx) => (
                    <TableRow key={invoice.order_id || invoice.invoice_id || idx}>
                      <TableCell className="font-mono text-sm font-medium">
                        {invoice.invoice_number || invoice.invoice_id || invoice.order_id}
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">{invoice.customer_name}</div>
                          <div className="text-sm text-muted-foreground">{invoice.customer_email}</div>
                          {invoice.customer_phone && <div className="text-sm text-muted-foreground">{invoice.customer_phone}</div>}
                        </div>
                      </TableCell>
                      <TableCell>{invoice.product_name}</TableCell>
                      <TableCell className="font-medium">
                        <div>{invoice.amount} EGP</div>
                        {(invoice.amount_eur || invoice.price_eur) && <div className="text-sm text-muted-foreground">{invoice.amount_eur || invoice.price_eur} EUR</div>}
                      </TableCell>
                      <TableCell>
                        <Badge variant="success" className="gap-1">
                          <CheckCircle className="w-3 h-3" />
                          مدفوع
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {invoice.created_at ? new Date(invoice.created_at).toLocaleDateString("ar-EG") : "-"}
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" onClick={() => downloadInvoice(invoice.invoice_id || invoice.order_id)} className="gap-1">
                          <Download className="w-4 h-4" />
                          PDF
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminInvoices;
